import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import './CustomerDetailsScreen.css';

export default function CustomerDetailsScreen() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch customers list to find the specific customer
        const customers = await api.getCustomers();
        const foundCustomer = customers.find(c => c.id === customerId);
        
        if (!foundCustomer) {
          setError('Customer not found');
          return;
        }

        setCustomer(foundCustomer);

        // Fetch customer files
        try {
          const customerFiles = await api.getCustomerFiles(customerId);
          setFiles(Array.isArray(customerFiles) ? customerFiles : []);
        } catch (fileError) {
          console.error('Failed to fetch customer files:', fileError);
          setFiles([]);
        }
      } catch (err) {
        console.error('Failed to fetch customer data:', err);
        setError(err.message || 'Failed to load customer data');
      } finally {
        setIsLoading(false);
      }
    };

    if (customerId) {
      fetchCustomerData();
    }
  }, [customerId]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatUuid = (uuid) => {
    if (!uuid) return '—';
    const parts = uuid.split('-');
    return parts[0] || '—';
  };

  const getStatusClass = (status) => {
    const statusMap = {
      Verified: 'vt-status--success',
      Pending: 'vt-status--warning',
      Rejected: 'vt-status--danger',
      'Under Review': 'vt-status--info',
      'PENDING_DOCUMENTS': 'vt-status--warning',
    };
    return statusMap[status] || '';
  };

  const getFileTypeLabel = (type) => {
    const typeMap = {
      'UTILITY_BILL': 'Utility Bill',
      'PASSPORT': 'Passport',
    };
    return typeMap[type] || type || 'Unknown';
  };

  const isImageFile = (fileType) => {
    return fileType && fileType.startsWith('image/');
  };

  if (isLoading) {
    return (
      <div className="vt-customerDetails">
        <div className="vt-pageHead">
          <button 
            className="vt-btn vt-btn--secondary" 
            onClick={() => navigate('/kyc')}
            style={{ marginBottom: '1rem' }}
          >
            ← Back to KYC
          </button>
          <h1 className="vt-pageTitle">Loading customer...</h1>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="vt-customerDetails">
        <div className="vt-pageHead">
          <button 
            className="vt-btn vt-btn--secondary" 
            onClick={() => navigate('/kyc')}
            style={{ marginBottom: '1rem' }}
          >
            ← Back to KYC
          </button>
          <h1 className="vt-pageTitle">Customer Details</h1>
        </div>
        <div className="vt-surface" style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--vt-danger)' }}>{error || 'Customer not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vt-customerDetails">
      <div className="vt-pageHead">
        <button 
          className="vt-btn vt-btn--secondary" 
          onClick={() => navigate('/kyc')}
          style={{ marginBottom: '1rem' }}
        >
          ← Back to KYC
        </button>
        <h1 className="vt-pageTitle">Customer Details</h1>
        <p className="vt-pageSub vt-muted">
          View customer information and uploaded documents
        </p>
      </div>

      <div className="vt-customerDetailsGrid">
        <section className="vt-customerInfo vt-surface">
          <h2 className="vt-sectionTitle">Customer Information</h2>
          <dl className="vt-detailsList">
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">ID</dt>
              <dd className="vt-detailsValue">{formatUuid(customer.id)}</dd>
            </div>
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">Email</dt>
              <dd className="vt-detailsValue">{customer.email || '—'}</dd>
            </div>
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">First Name</dt>
              <dd className="vt-detailsValue">{customer.firstname || '—'}</dd>
            </div>
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">Surname</dt>
              <dd className="vt-detailsValue">{customer.surname || '—'}</dd>
            </div>
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">Status</dt>
              <dd className="vt-detailsValue">
                <span className={['vt-status', getStatusClass(customer.status)].filter(Boolean).join(' ')}>
                  {customer.status || '—'}
                </span>
              </dd>
            </div>
            <div className="vt-detailsItem">
              <dt className="vt-detailsLabel">Created</dt>
              <dd className="vt-detailsValue">{formatDate(customer.created)}</dd>
            </div>
          </dl>
        </section>

        <section className="vt-customerFiles vt-surface">
          <h2 className="vt-sectionTitle">Uploaded Documents</h2>
          {files.length === 0 ? (
            <div className="vt-emptyState">
              <p className="vt-muted">No documents uploaded yet</p>
            </div>
          ) : (
            <div className="vt-filesGrid">
              {files.map((file) => (
                <div 
                  key={file.id} 
                  className="vt-fileCard"
                  onClick={() => navigate(`/customers/${customerId}/files/${file.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="vt-filePreview">
                    {file.secureUrl && isImageFile(file.fileType) ? (
                      <img 
                        src={file.secureUrl} 
                        alt={getFileTypeLabel(file.type)}
                        className="vt-fileImage"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="vt-filePlaceholder"
                      style={{ display: file.secureUrl && isImageFile(file.fileType) ? 'none' : 'flex' }}
                    >
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                    </div>
                  </div>
                  <div className="vt-fileInfo">
                    <div className="vt-fileType">{getFileTypeLabel(file.type)}</div>
                    <div className="vt-fileMeta vt-muted">
                      {file.fileType || 'Unknown type'}
                    </div>
                    {file.ocrMap && (
                      <div className="vt-fileOcrBadge" style={{ 
                        fontSize: '0.75rem', 
                        color: 'var(--vt-accent)', 
                        marginTop: '0.25rem',
                        fontWeight: '600'
                      }}>
                        OCR Available
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
